import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridSortModel,
  GridFilterModel,
  GridFilterItem,
  getGridStringOperators,
} from '@mui/x-data-grid';
import { ArrowLeft } from 'lucide-react';
import { useAppDispatch } from 'store/hooks';
import { showToast } from 'store/toastSlice';
import { userService } from 'components/api/users';
import { commonGridStyles } from 'styles/gridStyles';
import commonStyles from 'styles/common.module.scss';
import CustomPagination from 'components/common/pagination/pagination';
import styles from './games.module.scss';

interface SessionRoundsProps {
  userId: string;
  session: any;
  onBack: () => void;
}

export const SessionRounds: React.FC<SessionRoundsProps> = ({ userId, session, onBack }) => {
  const dispatch = useAppDispatch();
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortField, setSortField] = useState<string>('id');
  const [sortValue, setSortValue] = useState<'ASC' | 'DESC'>('DESC');
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [sessionBalance, setSessionBalance] = useState<any>(null);

  const customStringOperators = getGridStringOperators().filter(
    (operator) => operator.value === 'contains',
  );

  const fetchRounds = useCallback(async () => {
    try {
      setLoading(true);
      const response = await userService.getUserRounds(userId, {
        page,
        limit,
        sortField,
        sortValue,
        sessionId: session?.id || '',
        filters: '',
        ...filters,
      });

      if (response?.data?.docs) {
        setData(response.data.docs);
        setTotal(response.data.totalDocs || 0);
      }
    } catch (error: any) {
      console.error('Failed to fetch session rounds:', error);
      dispatch(
        showToast({
          text: error.response?.data?.message || 'Failed to fetch rounds',
          type: 'error',
        }),
      );
    } finally {
      setLoading(false);
    }
  }, [userId, session.id, page, limit, sortField, sortValue, filters, dispatch]);

  useEffect(() => {
    fetchRounds();
  }, [fetchRounds]);

  const fetchSessionBalance = useCallback(async () => {
    try {
      const response = await userService.getSessionBalance(userId, session.id);
      if (response?.data) {
        setSessionBalance(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch session balance:', error);
    }
  }, [userId, session.id]);

  useEffect(() => {
    fetchSessionBalance();
  }, [fetchSessionBalance]);

  const handleSortModelChange = useCallback((model: GridSortModel) => {
    if (model.length > 0) {
      setSortField(model[0].field);
      setSortValue(model[0].sort?.toUpperCase() as 'ASC' | 'DESC');
    } else {
      setSortField('id');
      setSortValue('DESC');
    }
  }, []);

  const handleFilterModelChange = useCallback((model: GridFilterModel) => {
    setFilterModel(model);
    const newFilters: Record<string, any> = {};
    model.items.forEach((item: GridFilterItem) => {
      if (item.field && item.value !== undefined && item.value !== '') {
        newFilters[item.field] = item.value;
      }
    });
    setFilters(newFilters);
    setPage(1);
  }, []);

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      width: 90,
      sortable: true,
      filterOperators: customStringOperators,
    },
    {
      field: 'gameTitle',
      headerName: 'Game',
      flex: 1,
      minWidth: 200,
      sortable: true,
      filterOperators: customStringOperators,
      headerClassName: 'align-left-header',
      cellClassName: 'align-left-cell',
      renderCell: (params) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            width: '100%',
            justifyContent: 'flex-start',
          }}
        >
          {params.row.gameImageUrl && (
            <Box
              component="img"
              src={params.row.gameImageUrl}
              alt={params.value}
              sx={{ width: 40, height: 28, borderRadius: 1, objectFit: 'cover' }}
            />
          )}
          <Typography noWrap variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'totalBet',
      headerName: 'Total Bet',
      width: 120,
      sortable: true,
      filterOperators: customStringOperators,
      renderCell: (params) => (
        <Typography sx={{ color: '#f92672', fontSize: '13px' }}>{params.value}</Typography>
      ),
    },
    {
      field: 'totalWin',
      headerName: 'Total Win',
      width: 120,
      sortable: true,
      filterOperators: customStringOperators,
      renderCell: (params) => (
        <Typography sx={{ color: '#a6e22e', fontSize: '13px' }}>{params.value}</Typography>
      ),
    },
    {
      field: 'currencyCode',
      headerName: 'Currency',
      width: 100,
      filterOperators: customStringOperators,
    },
    {
      field: 'createdAt',
      headerName: 'Created At',
      flex: 1,
      minWidth: 160,
      sortable: true,
      filterOperators: customStringOperators,
      renderCell: (params) => (params.value ? new Date(params.value).toLocaleString() : '-'),
    },
    {
      field: 'settledAt',
      headerName: 'Settled At',
      flex: 1,
      minWidth: 160,
      sortable: true,
      filterOperators: customStringOperators,
      renderCell: (params) => (params.value ? new Date(params.value).toLocaleString() : '-'),
    },
  ];

  const renderPagination = () => (
    <CustomPagination
      count={total}
      page={page - 1}
      onPageChange={(_, newPage) => setPage(newPage + 1)}
      rowsPerPage={limit}
      onRowsPerPageChange={(e) => {
        setLimit(parseInt(e.target.value, 10));
        setPage(1);
      }}
    />
  );

  return (
    <Box className={styles.container}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2, p: 3 }}>
        <IconButton
          onClick={onBack}
          sx={{ color: 'var(--text-primary)', bgcolor: 'rgba(255, 255, 255, 0.05)' }}
        >
          <ArrowLeft size={20} />
        </IconButton>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'var(--text-primary)', m: 0 }}>
            Session Logs: {session.gameTitle}
          </Typography>
          <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
            Session ID: {session.id} | User: {session.username}
          </Typography>
        </Box>
      </Box>

      {sessionBalance && (
        <Box sx={{ display: 'flex', gap: 3, mb: 2, px: 1 }}>
          <Typography variant="body1" sx={{ color: 'var(--text-secondary)' }}>
            Start Amount:{' '}
            <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
              {sessionBalance.startAmount ?? '...'} {sessionBalance.currencyCode}
            </span>
          </Typography>
          <Typography variant="body1" sx={{ color: 'var(--text-secondary)' }}>
            End Amount:{' '}
            <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
              {sessionBalance.endAmount ?? '...'} {sessionBalance.currencyCode}
            </span>
          </Typography>
        </Box>
      )}

      <Box className={commonStyles.gridWrapper}>
        {renderPagination()}
        <DataGrid
          rows={data}
          rowCount={total}
          paginationMode="server"
          sortingMode="server"
          filterMode="server"
          filterModel={filterModel}
          onSortModelChange={handleSortModelChange}
          onFilterModelChange={handleFilterModelChange}
          columns={columns}
          loading={loading}
          getRowId={(row) => row.id}
          sx={{
            ...commonGridStyles,
            '& .align-left-header .MuiDataGrid-columnHeaderTitleContainer': {
              justifyContent: 'flex-start !important',
            },
            '& .align-left-cell': {
              justifyContent: 'flex-start !important',
            },
          }}
          disableRowSelectionOnClick
          autoHeight
          hideFooterPagination
          slots={{
            noRowsOverlay: () => (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  p: 4,
                  color: 'var(--text-secondary)',
                }}
              >
                No rounds found for this session
              </Box>
            ),
          }}
        />
        {renderPagination()}
      </Box>
    </Box>
  );
};
